'use client';
import { forwardRef, type ReactNode } from 'react';
import { cn } from '@/app/utils';

export interface ReferenceCardProps {
  /** 卡片 ID，用于 ref 定位 */
  id?: string;
  /** 卡片内容 */
  children: ReactNode;
  /** 底部标签内容 */
  footer?: ReactNode;
  /** 删除按钮（hover 时显示在右上角） */
  deleteButton?: ReactNode;
  /** Hover 时显示的操作按钮（显示在内容区域中央） */
  hoverOperations?: ReactNode | ReactNode[];
  /** 右上角操作按钮（始终显示，移动端隐藏） */
  topRightSlot?: ReactNode;
  /** 扩展底部内容（hover 时显示在下方） */
  expandedFooter?: {
    left?: ReactNode;
    right?: ReactNode;
  };
  /** 是否支持拖拽 */
  draggable?: boolean;
  /** 拖拽事件处理器 */
  onDragStart?: (e: React.DragEvent<HTMLDivElement>) => void;
  onDragOver?: (e: React.DragEvent<HTMLDivElement>) => void;
  onDropCapture?: (e: React.DragEvent<HTMLDivElement>) => void;
  /** 自定义 className */
  className?: string;
  /** 内容区域自定义 className */
  contentClassName?: string;
  /** 底部标签自定义 className */
  footerClassName?: string;
}

/**
 * 参考卡片基础组件
 *
 * 提供统一的外壳结构，支持：
 * - 自定义内容渲染
 * - 底部标签
 * - 删除按钮
 * - Hover 操作按钮
 * - 拖拽支持
 */
export const ReferenceCard = forwardRef<HTMLDivElement, ReferenceCardProps>(
  (
    {
      id,
      children,
      footer,
      deleteButton,
      hoverOperations,
      topRightSlot,
      expandedFooter,
      draggable = false,
      onDragStart,
      onDragOver,
      onDropCapture,
      className,
      contentClassName,
      footerClassName,
    },
    ref,
  ) => {
    return (
      <div
        ref={ref}
        id={id}
        className={cn('group relative h-[66px] w-full', draggable && 'cursor-grab', className)}
        draggable={draggable}
        onDragStart={onDragStart}
        onDragOver={onDragOver}
        onDropCapture={onDropCapture}
      >
        {/* 内容区域 (带圆角和裁剪) */}
        <div
          className={cn(
            'relative h-full w-full rounded-8 border border-system-white24',
            expandedFooter &&
              'group-hover:-translate-y-1 transition-transform duration-200 group-hover:rounded-b-none group-hover:border-b-transparent',
            contentClassName,
          )}
        >
          {children}

          {/* Hover Operations (显示在内容区域中央，移动端隐藏) */}
          {hoverOperations && (
            <div className="absolute inset-0 hidden items-center justify-center gap-3 bg-black/40 group-hover:flex sm:hidden">
              {Array.isArray(hoverOperations) ? hoverOperations : hoverOperations}
            </div>
          )}

          {/* Top Right Slot (仅移动端显示) */}
          {topRightSlot && <div className="absolute top-1 right-1 z-10 hidden sm:flex">{topRightSlot}</div>}

          {/* Expanded Footer (hover 时从底部滑出) */}
          {expandedFooter && (
            <div className="-ml-[1px] relative flex h-6 w-[calc(100%+2px)] items-center justify-between rounded-b-8 border border-system-white24 border-t-transparent bg-system-black/80 px-1 opacity-0 transition-opacity duration-200 group-hover:z-10 group-hover:opacity-100">
              <div className="flex-1 truncate text-system-white text-xs">{expandedFooter.left}</div>
              <div className="flex shrink-0 items-center justify-end">{expandedFooter.right}</div>
            </div>
          )}
        </div>

        {/* Delete Button (hover 时显示在右上角，位于内容区域之外以避免被 overflow-hidden 裁剪) */}
        {deleteButton && (
          <div className="-right-2 -top-2 absolute z-10 hidden gap-1 group-hover:flex">{deleteButton}</div>
        )}

        {/* Footer Label (常规状态下的标签) */}
        {footer && (
          <div
            className={cn(
              'absolute bottom-1 left-1 z-10 h-4 rounded-6 bg-system-black px-1 text-system-white text-xs backdrop-blur-[2px]',
              expandedFooter && 'group-hover:opacity-0',
              footerClassName,
            )}
          >
            {footer}
          </div>
        )}
      </div>
    );
  },
);

ReferenceCard.displayName = 'ReferenceCard';
